import { Alert, Snackbar, SnackbarCloseReason } from '@mui/material';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { IFeedback } from '../../interfaces/IFeedback';

interface SnackbarContextType {
  showFeedback: (message: string, severity?: IFeedback['severity']) => void;
}

export const SnackbarContext = createContext<SnackbarContextType>({
  showFeedback: () => {},
});

interface SnackbarProviderProps {
  children: React.ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
}) => {
  const [feedback, setFeedback] = useState<IFeedback>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showFeedback = useCallback(
    (message: string, severity: IFeedback['severity'] = 'info') => {
      setFeedback({ open: true, message, severity });
    },
    []
  );

  const handleCloseSnackbar = (
    _: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setFeedback(prev => ({ ...prev, open: false }));
  };

  return (
    <SnackbarContext.Provider value={{ showFeedback }}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={feedback.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={(event: React.SyntheticEvent) => handleCloseSnackbar(event)}
          severity={feedback.severity}
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
      {children}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
